import { publicStorefrontPaths } from "modules/storefront/publicStorefrontRoute";
import { useEffect, useState } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  redirect,
  Route,
  RouterProvider,
} from "react-router-dom";

import { PublicReferralRewardLayout, PublicReferralRewardLayoutLoader } from "@/layouts";
import { AppShellLayout } from "@/layouts/AppShellLayout";
import { AuthShellLayout } from "@/layouts/AuthShellLayout";
import { DetailColumnLayout } from "@/layouts/DetailColumnLayout";
import { MobileOnlyLayout } from "@/layouts/MobileOnlyLayout";
import { PublicGiftCardLayout, publicGiftCardLayoutLoader } from "@/layouts/PublicGiftCardLayout";
import { useTranslation } from "@/core/i18n";
import { OnboardingProvider } from "@/modules/onboarding/context";
import ClaimReferralRewardPage, {
  claimReferralRewardLoader,
} from "@/pages/root/rewards/ClaimReferralRewardPage";
import ReferralRewardsPage, {
  referralRewardsPageLoader,
} from "@/pages/root/rewards/ReferralRewardsPage";
import BookingProviders, {
  bookingProvidersLoader,
} from "@/pages/root/shop/Booking/BookingProviders";
import {
  repeatCashActivityDetailsLoader,
  RepeatCashActivityDetailsPage,
} from "@/pages/root/wallet/RepeatCash/RepeatCashDetailsPage/RepeatCashActivityDetailsPage";
import Services from "@/services/Services";
import { getPlatformCurrent } from "api/methods";
import {
  setAnonymousBrowsingAllowed,
  setAnonymousCheckoutAllowed,
} from "modules/anonymousBrowsing/anonymousBrowsingState";
import { initializeBrowseLocationFromUrl } from "modules/anonymousBrowsing/anonymousLocation";
import { PersonalisationRoutes } from "modules/personalisation/routes/PersonalisationRoutes";
import Root, { queryClient, rootLoader } from "pages/Root";
import {
  accountDetailsAction,
  accountDetailsLoader,
  AccountDetailsPage,
} from "pages/root/account/account-details/AccountDetailsPage";
import { AccountLayout } from "pages/root/account/AccountLayout";
import AccountPage, { accountLoader } from "pages/root/account/AccountPage";
import {
  externalTransactionDetailsLoader,
  ExternalTransactionDetailsPage,
} from "pages/root/account/order-history/ExternalTransactionDetailsPage";
import {
  orderHistoryDetailsAction,
  orderHistoryDetailsLoader,
  OrderHistoryDetailsPage,
} from "pages/root/account/order-history/OrderHistoryDetailsPage";
import { OrderHistoryPage } from "pages/root/account/order-history/OrderHistoryPage";
import { RewardHistoryPage } from "pages/root/account/RewardHistoryPage";
import { AssessmentHistoryPage } from "pages/root/account/AssessmentHistoryPage";
import { AssessmentResultsPage } from "pages/root/account/assessment-results/AssessmentResultsPage";
import { VisitHistoryPage } from "pages/root/account/VisitHistoryPage";
import AnonymousLayout, { anonymousLoader } from "pages/root/AnonymousLayout";
import AppLayout, { AppLayoutError, appLayoutLoader } from "pages/root/AppLayout";
import AppLayoutNoBottomNav, { AppLayoutErrorNoBottomNav } from "pages/root/AppLayoutNoBottomNav";
import AuthenticatedOnboardingLayout, {
  AuthenticatedOnboardingLayoutError,
  authenticatedOnboardingLoader,
} from "pages/root/AuthenticatedOnboardingLayout";
import { changePlatformLoader, ChangePlatformPage } from "pages/root/ChangePlatformPage";
import CloseBrowserPaymentUpdatedPage from "pages/root/CloseBrowserPaymentUpdatedPage";
import { LINK_IN_BIO_PATH } from "modules/linkInBio/linkInBioRoute";
import { SAVE_CONTACT_PATH } from "modules/saveContact/saveContactRoute";
import { Deactivated, deactivatedLoader } from "pages/root/Deactivated";
import { FaqsLoader, FaqsPage } from "pages/root/faq";
import PublicShopPage, { publicShopLoader } from "pages/root/shop/public/PublicShopPage";
import ShopBrowseByConcernPage, {
  shopBrowseByConcernsLoader,
} from "pages/root/shop/ShopBrowseByConcernPage";
import ShopByConcernPage, { shopByConcernLoader } from "pages/root/shop/ShopByConcernPage";
import ShopCustomCategoryPage, {
  shopCustomCategoryLoader,
} from "pages/root/shop/ShopCustomCategory";
import {
  discoverLoader,
  lazyDiscover,
  lazyHome,
  lazyRewards,
  lazyShop,
  preloadPrimaryRoutes,
} from "./primaryRoutePreload";
import ShopMembershipsPage, { shopMembershipsLoader } from "pages/root/shop/ShopMembershipsPage";
import ShopRecommendedTreatmentsPage from "pages/root/shop/ShopRecommendedTreatmentsPage";
import ShopPage from "pages/root/ShopPage";
import { adminImpersonateLoader } from "pages/root/signIn/AdminImpersonationPage";
import {
  friendReferralLandingLoader,
  FriendReferralLandingPage,
} from "pages/root/signIn/FriendReferralLandingPage";
import { preRegisteredUserLoginLoader } from "pages/root/signIn/PreRegisteredUserLoginPage";
import V3DateOfBirthPage from "pages/root/signIn/V3DateOfBirthPage";
import V3EmailPage from "pages/root/signIn/V3EmailPage";
import V3GenderPage from "pages/root/signIn/V3GenderPage";
import V3GuestPracticePicker from "pages/root/signIn/V3GuestPracticePicker";
import V3LocationPicker from "pages/root/signIn/V3LocationPicker";
import V3NamePage from "pages/root/signIn/V3NamePage";
import V3NotificationsPage from "pages/root/signIn/V3NotificationsPage";
import V3PlatformPicker, { v3PlatformPickerLoader } from "pages/root/signIn/V3PlatformPicker";
import V3RewardsPage from "pages/root/signIn/V3RewardsPage";
import V3ShareFriendsPage, {
  v3ShareFriendsLoader,
  V3ShareFriendsPage_URL,
} from "pages/root/signIn/V3ShareFriendsPage";
import V3SignInPage, { v3SignInDisplayLoader } from "pages/root/signIn/V3SignInPage";
import V3SignupSourcePage from "pages/root/signIn/V3SignupSourcePage";
import V3VerificationCodePage from "pages/root/signIn/V3VerificationCodePage";
import {
  walletMembershipManageLoader,
  WalletMembershipsManagePage,
} from "pages/root/wallet/Memberships/WalletManageMembershipsPage";
import {
  walletMembershipCancelAction,
  walletMembershipCancelConfirmLoader,
  WalletMembershipCancelConfirmPage,
} from "pages/root/wallet/Memberships/WalletMembershipCancelConfirmPage";
import {
  walletMembershipCancelFreezeOfferLoader,
  WalletMembershipCancelFreezeOfferPage,
} from "pages/root/wallet/Memberships/WalletMembershipCancelFreezeOfferPage";
import {
  walletMembershipCancelLoader,
  WalletMembershipCancelPage,
} from "pages/root/wallet/Memberships/WalletMembershipCancelPage";
import {
  walletMembershipChangeLoader,
  WalletMembershipChangePage,
} from "pages/root/wallet/Memberships/WalletMembershipChangePage";
import {
  walletMembershipDunningCancelAction,
  WalletMembershipDunningCancelConfirmPage,
} from "pages/root/wallet/Memberships/WalletMembershipDunningCancelConfirmPage";
import {
  walletMembershipDunningCancelLoader,
  WalletMembershipDunningCancelPage,
} from "pages/root/wallet/Memberships/WalletMembershipDunningCancelPage";
import {
  walletMembershipFreezeAction,
  walletMembershipFreezeLoader,
  WalletMembershipFreezePage,
} from "pages/root/wallet/Memberships/WalletMembershipFreezePage";
import {
  walletMembershipPaymentMethodLoader,
  WalletMembershipPaymentMethodPage,
} from "pages/root/wallet/Memberships/WalletMembershipPaymentMethodPage";
import {
  WalletRepeatCashPage,
  walletRepeatCashPageLoader,
} from "pages/root/wallet/RepeatCash/WalletRepeatCashPage";
import WalletTreatmentsPage, { walletTreatmentsLoader } from "pages/root/wallet/Treatments";
import { treatmentLoader, TreatmentPage } from "pages/root/wallet/Treatments/TreatmentPage";
import WalletLayout from "pages/root/wallet/WalletLayout";
import WalletOfferPage, { walletOfferLoader } from "pages/root/wallet/WalletOfferPage";
import WalletOffersPage, { walletOffersLoader } from "pages/root/wallet/WalletOffersPage";
import { SponsorLayout } from "@/pages/sponsor/SponsorLayout";
import RootError from "pages/RootError";
import { shouldRevalidateSearch } from "pages/searchPageRequests";

const OnboardingWrapper = () => {
  return (
    <OnboardingProvider>
      <Outlet />
    </OnboardingProvider>
  );
};

const AppStartupShell = () => {
  const { t } = useTranslation();

  return (
    <main className="min-h-screen bg-white" aria-label={t("common.loading")} aria-busy="true">
      <header
        className="flex h-[calc(4rem+env(safe-area-inset-top))] items-center justify-between border-b border-gray-100 px-5"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="h-7 w-28 rounded bg-gray-100" />
        <div className="flex gap-3">
          <div className="h-8 w-8 rounded-full bg-gray-100" />
          <div className="h-8 w-8 rounded-full bg-gray-100" />
        </div>
      </header>
      <section className="space-y-4 px-5 py-8">
        <div className="h-8 w-2/3 rounded bg-gray-100" />
        <div className="h-4 w-full rounded bg-gray-100" />
        <div className="h-4 w-5/6 rounded bg-gray-100" />
        <div className="mt-8 h-40 rounded-2xl bg-gray-100" />
      </section>
    </main>
  );
};

const lazyV3GiftCardRedeem = async () => ({
  Component: (await import("pages/root/signIn/V3GiftCardRedeemPage")).default,
});
const lazyGiftCardDetails = async () => {
  const { default: Component, giftCardDetailsLoader: loader } =
    await import("pages/root/wallet/GiftCards/GiftCardDetailsPage");
  return { Component, loader };
};
const lazyShopProducts = async () => {
  const { default: Component, shopProductsLoader: loader } =
    await import("pages/root/shop/ShopProductsPage");
  return { Component, loader };
};
const lazyShopProductsList = async () => {
  const { default: Component, shopProductsListLoader: loader } =
    await import("pages/root/shop/ShopProductsListPage");
  return { Component, loader };
};
const lazyWalletProducts = async () => {
  const { default: Component, walletProductsLoader: loader } =
    await import("pages/root/wallet/Products/WalletProductsPage");
  return { Component, loader };
};
const lazyWalletProductOrderDetails = async () => {
  const { default: Component, walletProductOrderDetailsLoader: loader } =
    await import("pages/root/wallet/Products/WalletProductOrderDetailsPage");
  return { Component, loader };
};
const lazyWalletEarnedProductDetails = async () => {
  const { default: Component, walletEarnedProductDetailsLoader: loader } =
    await import("pages/root/wallet/Products/WalletEarnedProductDetailsPage");
  return { Component, loader };
};

// Profile completion routes that work for both anonymous and authenticated users
const ProfileCompletionRoutes = (
  <>
    <Route path="/signin/name" element={<V3NamePage />} handle={{ name: "Complete Profile" }} />
    <Route path="/signin/email" element={<V3EmailPage />} handle={{ name: "Complete Profile" }} />
    <Route
      path="/signin/dob"
      element={<V3DateOfBirthPage />}
      handle={{ name: "Complete Profile" }}
    />
    <Route path="/signin/gender" element={<V3GenderPage />} handle={{ name: "Complete Profile" }} />
    <Route
      path="/signin/source"
      element={<V3SignupSourcePage />}
      handle={{ name: "Complete Profile" }}
    />
  </>
);

const PublicOnboardingRoutes = (
  <Route path="/signin">
    <Route
      index
      element={<V3SignInPage />}
      handle={{ name: "Sign In" }}
      loader={v3SignInDisplayLoader}
    />
    <Route
      path="/signin/verification"
      element={<V3VerificationCodePage />}
      handle={{ name: "Sign In" }}
    />
    <Route
      path="/signin/platform-search"
      element={<V3PlatformPicker />}
      loader={v3PlatformPickerLoader}
      handle={{ name: "Sign Up" }}
    />
    <Route path="/signin/location" element={<V3LocationPicker />} handle={{ name: "Sign Up" }} />
    <Route
      path="/signin/browse-practice"
      element={<V3GuestPracticePicker />}
      handle={{ name: "Sign Up" }}
    />
    <Route
      path="/signin/gift-card-redeem"
      lazy={lazyV3GiftCardRedeem}
      handle={{ name: "Gift Card Claim" }}
    />
    <Route path="/signin/rewards" element={<V3RewardsPage />} handle={{ name: "Sign Up" }} />
    <Route
      path={V3ShareFriendsPage_URL}
      loader={v3ShareFriendsLoader}
      element={<V3ShareFriendsPage />}
      handle={{ name: "Sign Up" }}
    />
    <Route
      path="/signin/notifications"
      element={<V3NotificationsPage />}
      handle={{ name: "Sign Up" }}
    />
  </Route>
);

const lazyMembershipPDP = async () => {
  const {
    default: Component,
    MembershipPDPError,
    membershipPDPLoader,
  } = await import("pages/root/shop/MembershipPDP/MembershipPDP");
  return { Component, ErrorBoundary: MembershipPDPError, loader: membershipPDPLoader };
};
const lazyProductPDP = async () => {
  const {
    default: Component,
    ProductPDPError,
    productPDPLoader,
  } = await import("pages/root/shop/ProductPDP/ProductPDP");
  return { Component, ErrorBoundary: ProductPDPError, loader: productPDPLoader };
};
const lazyPackagePDP = async () => {
  const { default: Component, packagePDPLoader } =
    await import("pages/root/shop/PackagePDP/PackagePDP");
  return { Component, loader: packagePDPLoader };
};
const lazyRewardsDetail = async () => {
  const {
    default: Component,
    rewardsAction,
    rewardsLoader,
  } = await import("pages/root/rewards/RewardsPage");
  return { Component, action: rewardsAction, loader: rewardsLoader };
};
const lazyWalletMembership = async () => {
  const {
    default: Component,
    walletMembershipAction,
    walletMembershipLoader,
  } = await import("pages/root/wallet/Memberships/WalletMembershipPage");
  return { Component, action: walletMembershipAction, loader: walletMembershipLoader };
};
const lazySearch = async () => {
  const { default: Component, searchLoader: loader } = await import("pages/SearchPage");
  return { Component, loader };
};
const lazyGiftCardClaim = async () => {
  const { default: Component, claimGiftCardLoader: loader } =
    await import("pages/root/ClaimGiftCardPage");
  return { Component, loader };
};
const lazyGiftCardPurchase = async () => {
  const { default: Component, purchaseGiftCardLoader: loader } =
    await import("pages/root/gift-cards/GiftCardPurchasePage");
  return { Component, loader };
};
const lazyGiftCardCart = async () => {
  const { default: Component, giftCardCartLoader: loader } =
    await import("pages/root/gift-cards/GiftCardCartPage");
  return { Component, loader };
};
const lazyGiftCardCheckout = async () => {
  const { default: Component, purchaseGiftCardCheckoutLoader: loader } =
    await import("pages/root/gift-cards/GiftCardCheckoutPage");
  return { Component, loader };
};
const lazyGiftCardSuccess = async () => {
  const { default: Component, giftCardSuccessLoader: loader } =
    await import("pages/root/gift-cards/GiftCardSuccessPage");
  return { Component, loader };
};
const lazyCart = async () => {
  const {
    default: Component,
    cartLoader: loader,
    cartPageAction: action,
  } = await import("pages/root/cart/CartPage");
  return { Component, loader, action };
};
const lazyAnonymousCheckout = async () => {
  const { default: Component, anonymousCheckoutLoader: loader } =
    await import("pages/root/cart/AnonymousCheckoutPage");
  const { cartPageAction: action } = await import("pages/root/cart/CartPage");
  return { Component, loader, action };
};
const lazyCheckoutElement = async () => {
  const { default: Component, checkoutElementLoader: loader } =
    await import("pages/root/cart/CheckoutElementPage");
  return { Component, loader };
};
const lazyCheckoutSuccess = async () => {
  const { default: Component, CheckoutSuccessPageLoader: loader } =
    await import("pages/root/cart/CheckoutSuccessPage");
  return { Component, loader };
};
const lazyBeautifiReturn = async () => {
  const { default: Component, BeautifiReturnPageLoader: loader } =
    await import("pages/root/cart/BeautifiReturnPage");
  return { Component, loader };
};
const lazyMembershipRewards = async () => {
  const { default: Component, MembershipRewardsSelectionPageLoader: loader } =
    await import("pages/root/cart/MembershipRewardsSelectionPage");
  return { Component, loader };
};
const lazyCheckoutReward = async () => {
  const { CheckoutRewardTrackerPage: Component, checkoutRewardTrackerLoader: loader } =
    await import("pages/root/cart/CheckoutRewardTrackerPage");
  return { Component, loader };
};
const lazyCheckInRedeem = async () => {
  const { CheckInRedeemPage: Component, checkInRedeemLoader: loader } =
    await import("pages/root/checkin/CheckInRedeemPage");
  return { Component, loader };
};
const lazyReturns = async () => {
  const { default: Component, ReturnsLoader: loader } =
    await import("pages/root/return/ReturnsPage");
  return { Component, loader };
};
const lazySelectedItemReturn = async () => {
  const { default: Component, SelectedItemReturnLoader: loader } =
    await import("pages/root/return/SelectedItemReturnFlow/SelectedItemReturnFlow");
  return { Component, loader };
};
const lazyScanCheckIn = async () => ({
  Component: (await import("pages/root/ScanCheckInPage")).default,
});
const lazyScanOffer = async () => {
  const { default: Component, ScanRedeemOfferInLocationLoader: loader } =
    await import("pages/root/ScanRedeemOfferInLocationPage");
  return { Component, loader };
};
const lazyScanReward = async () => ({
  Component: (await import("pages/root/ScanRedeemRewardInLocationPage")).default,
});
const lazyScanTreatment = async () => ({
  Component: (await import("pages/root/wallet/Treatments/ScanRedeemTreatmentPage")).default,
});
const lazyScanRetailOrder = async () => ({
  Component: (await import("pages/root/ScanRedeemRetailProductsOrder")).default,
});
const lazyScanPickup = async () => ({
  Component: (await import("pages/root/ScanPickupRetailProductsItem")).default,
});
const lazyLinkInBio = async () => ({
  Component: (await import("@/modules/linkInBio/LinkInBioPage")).LinkInBioPage,
});
const lazySponsorSignIn = async () => ({
  Component: (await import("@/pages/sponsor/SponsorSignInPage")).SponsorSignInPage,
});
const lazySponsorOverview = async () => ({
  Component: (await import("@/pages/sponsor/SponsorOverviewPage")).SponsorOverviewPage,
});
const lazySponsorPlans = async () => ({
  Component: (await import("@/pages/sponsor/SponsorPlansPage")).SponsorPlansPage,
});
const lazySponsorMembers = async () => ({
  Component: (await import("@/pages/sponsor/SponsorMembersPage")).SponsorMembersPage,
});
const lazySponsorBilling = async () => ({
  Component: (await import("@/pages/sponsor/SponsorBillingPage")).SponsorBillingPage,
});
const lazyShopTreatments = async () => {
  const { default: Component, shopTreatmentsLoader: loader } =
    await import("pages/root/shop/ShopTreatmentsPage");
  return { Component, loader };
};
const lazyShopPackages = async () => {
  const { default: Component, shopPackagesLoader: loader } =
    await import("pages/root/shop/ShopPackagesPage");
  return { Component, loader };
};
const lazyShopGiftCards = async () => {
  const { default: Component, shopGiftCardsLoader: loader } =
    await import("pages/root/shop/ShopGiftCardsPage");
  return { Component, loader };
};
const lazyWalletGiftCards = async () => {
  const { WalletGiftCardsPage: Component, walletGiftCardsPageLoader: loader } =
    await import("pages/root/wallet/GiftCards/WalletGiftCardsPage");
  return { Component, loader };
};

const lazySaveContact = async () => {
  const { SaveContactPage: Component, saveContactLoader: loader } =
    await import("modules/saveContact/SaveContactPage");
  return { Component, loader };
};

const AuthenticatedOnboardingRoutes = <Route path="/signin"></Route>;

const publicReferralRewardRoutes = (
  <Route
    element={<PublicReferralRewardLayout />}
    loader={PublicReferralRewardLayoutLoader}
    path="/referral-rewards"
  >
    <Route
      path="/referral-rewards/:referralCode"
      element={<ClaimReferralRewardPage />}
      loader={claimReferralRewardLoader}
      handle={{ name: "Referral Reward" }}
    />
  </Route>
);

const publicGiftCardRoutes = (
  <Route element={<PublicGiftCardLayout />} loader={publicGiftCardLayoutLoader} path="/gift-cards">
    <Route path="/gift-cards/claim" lazy={lazyGiftCardClaim} />
    <Route path="/gift-cards/purchase" lazy={lazyGiftCardPurchase} />

    <Route path="/gift-cards/cart" lazy={lazyGiftCardCart} />

    <Route path="/gift-cards/checkout" lazy={lazyGiftCardCheckout} />

    <Route path="/gift-cards/success" lazy={lazyGiftCardSuccess} />
  </Route>
);

const anonymousRoutes = (
  <Route element={<MobileOnlyLayout />}>
    <Route element={<AnonymousLayout />} loader={anonymousLoader}>
      {/* Sign-in and onboarding become a centred card on desktop; the public
          catalogue below stays a full-width page. */}
      <Route element={<AuthShellLayout />}>
        <Route element={<OnboardingWrapper />}>{PublicOnboardingRoutes}</Route>
        <Route element={<OnboardingWrapper />}>{ProfileCompletionRoutes}</Route>
      </Route>

      <Route
        // This page route should be kept in sync with the admin app.
        // The admin app hardcodes a link to this page in
        // Shared/RepeatMD.Shared/Commands/Admin/Users/RequestMagicLinkForPlatformUserInPatientAppCommand.cs
        path="/admin-impersonate"
        loader={adminImpersonateLoader}
        handle={{ name: "Admin Impersonate" }}
      />
      <Route
        // This page route should be kept in sync with the admin app.
        // The admin app hardcodes a link to this page in
        // Shared/RepeatMD.Shared/Commands/Admin/Users/CreateCustomPlanCommand.cs
        path="/pre-registered-user-login"
        loader={preRegisteredUserLoginLoader}
        handle={{ name: "Login" }}
      />
      <Route
        path="/referral/:referralCode"
        element={<FriendReferralLandingPage />}
        loader={friendReferralLandingLoader}
        handle={{ name: "Sign In Referral" }}
      />
      <Route path="/v1migration" loader={() => redirect("/signin")} handle={{ name: "Sign In" }} />
      <Route path="/stripe-landing/close-browser" element={<CloseBrowserPaymentUpdatedPage />} />
      <Route path="/shop/public" loader={publicShopLoader} element={<PublicShopPage />} />
    </Route>
  </Route>
);

const authenticatedRoutes = (
  <Route element={<AppShellLayout />}>
    <Route element={<AuthShellLayout />}>
      <Route element={<OnboardingWrapper />}>{ProfileCompletionRoutes}</Route>
    </Route>
    <Route element={<AppLayout />} loader={appLayoutLoader} errorElement={<AppLayoutError />}>
      <Route path={publicStorefrontPaths.home} lazy={lazyHome} handle={{ name: "Home" }} />
      <Route
        path={publicStorefrontPaths.discover}
        loader={discoverLoader}
        lazy={lazyDiscover}
        handle={{ name: "Discover" }}
      />
      <Route path={publicStorefrontPaths.shop} element={<ShopPage />}>
        <Route index lazy={lazyShop} handle={{ name: "Shop" }} />
        <Route
          path="/shop/recommended-treatments"
          element={<ShopRecommendedTreatmentsPage />}
          handle={{ name: "Recommended for you" }}
        />
        <Route
          path={publicStorefrontPaths.shopMemberships}
          loader={shopMembershipsLoader}
          element={<ShopMembershipsPage />}
          handle={{ name: "Shop Memberships" }}
        />
        <Route
          path={publicStorefrontPaths.shopTreatments}
          lazy={lazyShopTreatments}
          handle={{ name: "Shop Treatments" }}
        />
        <Route
          path={publicStorefrontPaths.shopProducts}
          lazy={lazyShopProducts}
          handle={{ name: "Shop Products" }}
        />
        <Route
          path={publicStorefrontPaths.shopPackages}
          lazy={lazyShopPackages}
          handle={{ name: "Shop Packages" }}
        />
        <Route
          path="/shop/concerns"
          loader={shopBrowseByConcernsLoader}
          element={<ShopBrowseByConcernPage />}
          handle={{ name: "Shop Browse by Concern" }}
        />
        <Route
          path="/shop/gift-cards"
          lazy={lazyShopGiftCards}
          handle={{ name: "Shop Gift Cards" }}
        />
        <Route
          path={publicStorefrontPaths.shopCategory}
          lazy={lazyShopPackages}
          handle={{ name: "Shop Categories" }}
        />
        <Route
          path={publicStorefrontPaths.shopCustomCategory}
          loader={shopCustomCategoryLoader}
          element={<ShopCustomCategoryPage />}
        />
      </Route>
      <Route
        path={publicStorefrontPaths.productList}
        lazy={lazyShopProductsList}
        handle={{ name: "Shop Products List" }}
      />
      <Route
        path="/shop/concern/:id"
        loader={shopByConcernLoader}
        element={<ShopByConcernPage />}
        handle={{ name: "Shop By Concern" }}
      />
      <Route path={publicStorefrontPaths.membership} lazy={lazyMembershipPDP} />
      <Route path={publicStorefrontPaths.membershipWithSource} lazy={lazyMembershipPDP} />
      <Route path={publicStorefrontPaths.product} lazy={lazyProductPDP} />
      <Route path={publicStorefrontPaths.productWithSource} lazy={lazyProductPDP} />
      <Route path={publicStorefrontPaths.package} lazy={lazyPackagePDP} />
      <Route element={<DetailColumnLayout />}>
        <Route
          path="/booking/:id/providers"
          element={<BookingProviders />}
          loader={bookingProvidersLoader}
        />
      </Route>
      <Route
        path="/booking/:id/locations"
        loader={({ params, request }) => {
          const url = new URL(request.url);
          return redirect(`/booking/${params.id}/providers${url.search}`);
        }}
      />
      <Route path="/rewards">
        <Route index lazy={lazyRewards} handle={{ name: "Rewards" }} />
        <Route
          path="/rewards/:rewardId"
          lazy={lazyRewardsDetail}
          handle={{ name: "Reward Details" }}
        />
        {/* <Route
            path="/rewards/track-referrals"
            element={<TrackReferralsPage />}
            loader={rewardsLoader}
            handle={{ name: "Track Referrals" }}
          /> */}
        <Route
          path="/rewards/referrals"
          element={<ReferralRewardsPage />}
          loader={referralRewardsPageLoader}
        />
      </Route>

      <Route path="/wallet">
        <Route index element={<WalletLayout />} />
        <Route element={<WalletLayout />}>
          <Route
            path="/wallet/treatments"
            loader={walletTreatmentsLoader}
            element={<WalletTreatmentsPage />}
            handle={{ name: "Wallet Treatments" }}
          />
          <Route
            path="/wallet/offers"
            loader={walletOffersLoader}
            element={<WalletOffersPage />}
            handle={{ name: "Wallet Offers" }}
          />
          <Route
            path="/wallet/products"
            lazy={lazyWalletProducts}
            handle={{ name: "Wallet Products" }}
          />
          <Route
            path="/wallet/memberships"
            lazy={lazyWalletMembership}
            handle={{ name: "Wallet Memberships" }}
          />
          <Route
            path="/wallet/repeatcash"
            loader={walletRepeatCashPageLoader}
            element={<WalletRepeatCashPage />}
            handle={{ name: "Wallet RepeatCash" }}
          />
          <Route
            path="/wallet/giftcards"
            lazy={lazyWalletGiftCards}
            handle={{ name: "Wallet Gift Cards" }}
          />
          <Route
            path="/wallet/giftcards/:giftCardTransferId"
            lazy={lazyGiftCardDetails}
            handle={{ name: "Gift Card Details" }}
          />
        </Route>
        <Route
          path="/wallet/offers/:offerId"
          element={<WalletOfferPage />}
          loader={walletOfferLoader}
          handle={{ name: "Wallet Offer Details" }}
        />
        <Route
          path="/wallet/treatments/:packageId"
          element={<TreatmentPage />}
          loader={treatmentLoader}
          handle={{ name: "Treatment Details" }}
        />
        <Route
          path="/wallet/products/order/:orderId"
          lazy={lazyWalletProductOrderDetails}
          handle={{ name: "Order Details" }}
        />
        <Route
          path="/wallet/products/earned/:packageId"
          lazy={lazyWalletEarnedProductDetails}
          handle={{ name: "Earned Product Details" }}
        />
        <Route
          path="/wallet/memberships/manage"
          loader={walletMembershipManageLoader}
          element={<WalletMembershipsManagePage />}
          handle={{ name: "Manage memberships" }}
        />
        <Route
          path="/wallet/memberships/manage/:platformUserMembershipId"
          loader={walletMembershipCancelLoader}
          element={<WalletMembershipCancelPage />}
          handle={{ name: "Cancel My Membership" }}
        />
        <Route
          path="/wallet/memberships/manage/:platformUserMembershipId/change"
          loader={walletMembershipChangeLoader}
          element={<WalletMembershipChangePage />}
          handle={{ name: "Change Membership" }}
        />
        <Route
          path="/wallet/memberships/manage/:platformUserMembershipId/dunning"
          loader={walletMembershipDunningCancelLoader}
          element={<WalletMembershipDunningCancelPage />}
          handle={{ name: "Cancel My Membership" }}
        />
        <Route
          path="/wallet/memberships/manage/:platformUserMembershipId/payment-method"
          loader={walletMembershipPaymentMethodLoader}
          element={<WalletMembershipPaymentMethodPage />}
          handle={{ name: "Update membership payment method" }}
        />
        <Route
          path="/wallet/memberships/manage/:platformUserMembershipId/dunning/cancel"
          loader={walletMembershipDunningCancelLoader}
          action={walletMembershipDunningCancelAction}
          element={<WalletMembershipDunningCancelConfirmPage />}
          handle={{ name: "Cancel My Membership" }}
        />
        <Route
          path="/wallet/memberships/manage/:platformUserMembershipId/cancel"
          loader={walletMembershipCancelConfirmLoader}
          action={walletMembershipCancelAction}
          element={<WalletMembershipCancelConfirmPage />}
          handle={{ name: "Cancel My Membership" }}
        />
        <Route
          path="/wallet/memberships/manage/:platformUserMembershipId/cancel/freeze-offer"
          loader={walletMembershipCancelFreezeOfferLoader}
          element={<WalletMembershipCancelFreezeOfferPage />}
          handle={{ name: "Cancel My Membership" }}
        />
        <Route
          path="/wallet/memberships/manage/:platformUserMembershipId/freeze"
          loader={walletMembershipFreezeLoader}
          action={walletMembershipFreezeAction}
          element={<WalletMembershipFreezePage />}
          handle={{ name: "Freeze My Membership" }}
        />
        {/* Handle redirects from old /my-treatments page */}
        <Route path="/wallet/my-treatments" loader={() => redirect("/wallet/treatments")} />
        <Route
          path="/wallet/my-treatments/*"
          loader={({ params }) => {
            const path = params["*"];
            return redirect(`/wallet/treatments/${path}`);
          }}
        />
        {/* Handle redirects from old /membership page */}
        <Route path="/wallet/membership" loader={() => redirect("/wallet/memberships")} />
        <Route
          path="/wallet/membership/*"
          loader={({ params }) => {
            const path = params["*"];
            return redirect(`/wallet/memberships/${path}`);
          }}
        />
      </Route>

      <Route path="/account" element={<AccountLayout />}>
        <Route index loader={accountLoader} element={<AccountPage />} />
        <Route
          path="/account/details"
          action={accountDetailsAction}
          loader={accountDetailsLoader}
          element={<AccountDetailsPage />}
          handle={{ name: "Account Details" }}
        />
        <Route path="/account/visits">
          <Route index element={<VisitHistoryPage />} handle={{ name: "Visit History" }} />
        </Route>
        <Route path="/account/orders">
          <Route index element={<OrderHistoryPage />} handle={{ name: "Order History" }} />
        </Route>
        <Route path="/account/orders/:shopDisplayOrderId">
          <Route
            index
            loader={orderHistoryDetailsLoader}
            action={orderHistoryDetailsAction}
            element={<OrderHistoryDetailsPage />}
            handle={{ name: "Order History Details" }}
          />
        </Route>
        <Route path="/account/external-orders/:externalTransactionId">
          <Route
            index
            loader={externalTransactionDetailsLoader}
            element={<ExternalTransactionDetailsPage />}
            handle={{ name: "External Transaction Details" }}
          />
        </Route>
        <Route
          path="/account/reward-history"
          element={<RewardHistoryPage />}
          handle={{ name: "Reward History" }}
        />

        <Route path="/account/assessments">
          <Route
            index
            element={<AssessmentHistoryPage />}
            handle={{ name: "Treatment Try-On History" }}
          />
        </Route>
        <Route path="/account/assessments/:assessmentId">
          <Route
            index
            element={<AssessmentResultsPage />}
            handle={{ name: "Assessment Results" }}
          />
        </Route>

        {/* Handle redirects from old /account/wallet page */}
        <Route path="/account/wallet" loader={() => redirect("/wallet/repeatcash")} />
        <Route path="/account/wallet/repeatcash/:transactionId">
          <Route
            index
            loader={repeatCashActivityDetailsLoader}
            element={<RepeatCashActivityDetailsPage />}
          />
        </Route>

        {/* Redirect /account/settings/* to /account/* */}
        <Route path="/account/settings" loader={() => redirect("/account")} />
        <Route
          path="/account/settings/*"
          loader={({ params }) => {
            const path = params["*"];
            return redirect(`/account/${path}`);
          }}
        />

        {/* Catch-all redirect for anything else under /account/* to /wallet/* */}
        <Route
          path="/account/*"
          loader={({ params }) => {
            const path = params["*"];
            return redirect(`/wallet/${path}`);
          }}
        />
      </Route>
      <Route path="/search" lazy={lazySearch} shouldRevalidate={shouldRevalidateSearch} />
      {/* The pre-Algolia search route: links in the wild keep their query. */}
      <Route
        path="/search-services"
        loader={({ request }) => redirect(`/search${new URL(request.url).search}`)}
      />
      <Route path="/faqs" loader={FaqsLoader} element={<FaqsPage />} handle={{ name: "FAQS" }} />
    </Route>
  </Route>
);

const noAuthCheckRoutes = (
  <Route element={<MobileOnlyLayout />}>
    {/*
      Everyone lands on the storefront, logged in or not. A visitor a practice lets browse should see the
      shop rather than a login wall — the sign-in prompts live on the surfaces that actually need an account
      (wallet, rewards, checkout). When the practice has anonymous browsing off there is no token to load,
      so `/home` 401s and AppLayoutError sends them to /signin with the return path already captured.
    */}
    <Route index loader={() => redirect("/home")} />
    <Route
      path="/app/*"
      loader={(request) => {
        const path = request.params["*"];
        return redirect(`/${path}`);
      }}
    />
    <Route path="/deactivated" element={<Deactivated />} loader={deactivatedLoader} />
    <Route
      path="/platform-selector"
      loader={changePlatformLoader}
      element={<ChangePlatformPage />}
    />
    <Route
      path="/stripe-landing"
      loader={() => redirect(`/wallet/memberships/manage${globalThis.location.search}`)}
    />
  </Route>
);

const authenticatedRoutesNoBottomNav = (
  <Route element={<AppShellLayout />}>
    <Route
      element={<AppLayoutNoBottomNav />}
      loader={appLayoutLoader}
      errorElement={<AppLayoutErrorNoBottomNav />}
    >
      <Route element={<DetailColumnLayout />}>
        <Route path="/custom-plan/:id" lazy={lazyPackagePDP} />
      </Route>
      <Route
        path="/discoverNoBottomNav"
        loader={discoverLoader}
        lazy={lazyDiscover}
        handle={{ name: "Discover" }}
      />
      <Route path="/cart">
        <Route index lazy={lazyCart} handle={{ name: "Your Shopping Cart" }} />
        <Route path="/cart/checkout" lazy={lazyAnonymousCheckout} handle={{ name: "Checkout" }} />
        <Route
          path="/cart/checkout/element"
          lazy={lazyCheckoutElement}
          handle={{ name: "Checkout Element" }}
        />
        <Route
          path="/cart/checkout/success"
          lazy={lazyCheckoutSuccess}
          handle={{ name: "Checkout Success" }}
        />
        <Route
          path="/cart/checkout/beautifi-return"
          lazy={lazyBeautifiReturn}
          handle={{ name: "Beautifi Return" }}
        />
        <Route
          path="/cart/checkout/membership-rewards"
          lazy={lazyMembershipRewards}
          handle={{ name: "Membership Rewards Selection" }}
        />
        <Route
          path="/cart/checkout/reward"
          lazy={lazyCheckoutReward}
          handle={{ name: "Checkout Reward Tracker" }}
        />
      </Route>
      <Route
        path="/checkin/redeem"
        lazy={lazyCheckInRedeem}
        handle={{ name: "Check In Redemption" }}
      />
      <Route path="/products/order/:id/return" lazy={lazyReturns} />
      <Route path="/products/order/:id/return/:itemId" lazy={lazySelectedItemReturn} />
      {/* Scan routes without footer */}
      <Route path="/scan-checkin-page" lazy={lazyScanCheckIn} />
      <Route path="/scan-redeem-offer-in-location" lazy={lazyScanOffer} />
      <Route path="/scan-redeem-reward-in-location" lazy={lazyScanReward} />
      <Route path="/scan-redeem-treatment-in-location" lazy={lazyScanTreatment} />
      <Route path="/scan-redeem-retail-products-order" lazy={lazyScanRetailOrder} />
      <Route path="/scan-pickup-retail-products-item" lazy={lazyScanPickup} />
    </Route>
  </Route>
);

const authenticatedOnboardingRoutes = (
  <Route element={<MobileOnlyLayout />}>
    <Route
      element={
        <OnboardingProvider>
          <AuthenticatedOnboardingLayout />
        </OnboardingProvider>
      }
      loader={authenticatedOnboardingLoader}
      errorElement={<AuthenticatedOnboardingLayoutError />}
    >
      <Route element={<AuthShellLayout />}>{AuthenticatedOnboardingRoutes}</Route>
    </Route>
  </Route>
);

/**
 * Texted to patients so they can save the practice's contact card. Outside every app layout — no
 * navigation, no sign-in or anonymous-browsing gate — so a signed-out visitor sees only the card.
 */
const saveContactRoute = (
  <Route path={SAVE_CONTACT_PATH} lazy={lazySaveContact} handle={{ name: "Save Contact" }} />
);

/**
 * A practice's public link-in-bio page. Outside every app layout: no tab bar, no auth loaders, and no
 * `MobileOnlyLayout` border — the page sizes its own column and paints its own full-bleed backdrop.
 * The API is anonymous, so it renders signed out even when the practice has anonymous browsing off.
 */
const linkInBioRoutes = (
  <Route path={LINK_IN_BIO_PATH} lazy={lazyLinkInBio} handle={{ name: "Link In Bio" }} />
);

/**
 * The employer-facing sponsor portal.
 *
 * Deliberately NOT wrapped in `MobileOnlyLayout`: that layout pins content to a 390px column from
 * `sm:` up, which is right for the patient app and wrong here — a sponsor administers a benefit
 * from a desk as often as from a phone, and a member table inside a phone-width column on a
 * desktop is unusable. `SponsorShell` supplies real desktop and mobile layouts instead.
 *
 * These routes are also outside the patient `AppLayout` loaders, because a sponsor administrator
 * has no `platform_users` row for those loaders to resolve. `SponsorLayout` does the auth check.
 */
const sponsorPortalRoutes = (
  <Route path="/sponsor">
    <Route path="/sponsor/signin" lazy={lazySponsorSignIn} handle={{ name: "Sponsor Sign In" }} />
    <Route element={<SponsorLayout />}>
      <Route index element={null} handle={{ name: "Sponsor Portal" }} />
      <Route
        path="/sponsor/:platformSponsorId"
        lazy={lazySponsorOverview}
        handle={{ name: "Sponsor Overview" }}
      />
      <Route
        path="/sponsor/:platformSponsorId/plans"
        lazy={lazySponsorPlans}
        handle={{ name: "Sponsor Plans" }}
      />
      <Route
        path="/sponsor/:platformSponsorId/members"
        lazy={lazySponsorMembers}
        handle={{ name: "Sponsor Members" }}
      />
      <Route
        path="/sponsor/:platformSponsorId/billing"
        lazy={lazySponsorBilling}
        handle={{ name: "Sponsor Billing" }}
      />
    </Route>
  </Route>
);

export function AppRouter() {
  const [router, setRouter] = useState<ReturnType<typeof createBrowserRouter> | null>(null);

  useEffect(() => {
    const initRouter = async () => {
      const token = await Services.auth.loadToken();
      // Route loaders run in parallel, so the anonymous-browsing mode must be
      // derived from the token and the platform flag before the router exists.
      const applyPlatformToggles = queryClient
        .ensureQueryData({
          ...getPlatformCurrent.keyed(),
          queryFn: () => getPlatformCurrent().catch(() => null),
        })
        .then((platform) => {
          setAnonymousBrowsingAllowed(
            platform?.platformSettings?.anonymousBrowsingEnabled === true,
          );
          setAnonymousCheckoutAllowed(
            platform?.platformSettings?.anonymousCheckoutEnabled === true,
          );
        });
      // The mode is `allowed && !hasAuthToken`, so a visitor who already holds a token is not
      // browsing anonymously whatever the platform says. Only a signed-out visitor has to wait
      // for the flag; for everyone else the toggles land in the background and the router is
      // built now, instead of behind a request that cannot change the answer.
      if (!token) await applyPlatformToggles;
      // Put the visitor's chosen location back on the outgoing requests before any loader runs.
      initializeBrowseLocationFromUrl();
      const newRouter = createBrowserRouter(
        createRoutesFromElements(
          <Route
            path={publicStorefrontPaths.root}
            loader={rootLoader}
            element={<Root />}
            errorElement={<RootError />}
          >
            {noAuthCheckRoutes}
            {anonymousRoutes}
            {authenticatedRoutes}
            {authenticatedRoutesNoBottomNav}
            {authenticatedOnboardingRoutes}
            {publicGiftCardRoutes}
            {publicReferralRewardRoutes}
            {PersonalisationRoutes}
            {sponsorPortalRoutes}
            {linkInBioRoutes}
            {saveContactRoute}
          </Route>,
        ),
      );
      setRouter(newRouter);
      preloadPrimaryRoutes(globalThis.location.pathname);
    };

    initRouter();
  }, []);

  if (!router) {
    return <AppStartupShell />;
  }

  return <RouterProvider router={router} fallbackElement={<AppStartupShell />} />;
}
